import './css/SubscriptionPlanTable.css';

import type {ClickSubscriptionPlanTableProperties} from '@gaia/ampli';
import {useSubscriptionPlanDiscountedAmount} from '@gaia/hooks/useSubscriptionPlans';
import {gaia, gaiaPremium, outsidePlusBlackText} from '@gaia/images/logos';
import {getGaiaFreeURL, getGaiaPremiumURL, getOutsidePlusURL} from '@gaia/urls';
import {formatPrice} from '@gaia/utils/gaia/formatPrice';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import times from 'lodash/times';

interface SubscriptionPlanTableProps {
    location: ClickSubscriptionPlanTableProperties['subscriptionPlanTableLocation'];
}

export function SubscriptionPlanTable({location}: SubscriptionPlanTableProps) {
    const gaiaPremiumAnnualPrice = useSubscriptionPlanDiscountedAmount('GaiaGPSPremiumStripe2018');
    const gaiaPremiumMonthlyPrice = Math.floor(gaiaPremiumAnnualPrice / 12);
    const isGaiaPremiumFullPrice = gaiaPremiumAnnualPrice === 3999;

    const outsidePlusAnnualPrice = useSubscriptionPlanDiscountedAmount('O+DigitalStripe');
    const outsidePlusMonthlyPrice = Math.floor(outsidePlusAnnualPrice / 12);
    const isOutsidePlusFullPrice = outsidePlusAnnualPrice === 5999;

    return (
        <section data-location={location} className={'plan-matrix'}>
            <ul className='headings'>
                <h2 className='col-heading'>Features</h2>
                <li>Basic maps</li>
                <li>Record activities</li>
                <li>Create routes</li>
                <li>300+ maps</li>
                <li>Offline use</li>
                <li>Weather &amp; terrain features</li>
                <li>Trailforks Pro GPS app</li>
                <li>Expert-led online courses on Outside Learn</li>
                <li>Premium access to award-winning films, shows, and live TV on Outside Watch</li>
                <li>Full digital access to 15 publications in the Outside Network</li>
                <li>Plus, get more benefits across the Outside Network</li>
            </ul>
            <ul className='features gaia-basic'>
                <h2 className='col-heading'>
                    <img width={152} height={32} className='logo' src={gaia} alt='Gaia logo' />
                    <p className='price'>FREE</p>
                </h2>
                <li className='check'>
                    <Check />
                </li>
                <li className='check'>
                    <Check />
                </li>
                <li className='check'>
                    <Check />
                </li>

                {times(8, (n) => (
                    <li key={n} />
                ))}
                {location === 'go-premium-modal' ? (
                    <div />
                ) : (
                    <div className='cta'>
                        <a
                            onClick={() => {
                                ampli.clickSubscriptionPlanTable({
                                    subscriptionPlanTableButton: 'Free',
                                    subscriptionPlanTableLocation: location,
                                });
                            }}
                            href={getGaiaFreeURL}
                        >
                            Get Gaia GPS Free
                        </a>
                    </div>
                )}
            </ul>
            <ul className='features gaia-premium'>
                <h2 className='col-heading'>
                    <img
                        width={152}
                        height={32}
                        className='logo'
                        src={gaiaPremium}
                        alt='Gaia logo'
                    />
                    <p className={`price ${isGaiaPremiumFullPrice ? '' : 'discounted'}`}>
                        {formatPrice(gaiaPremiumMonthlyPrice)}
                        <small>
                            <b>/</b>mo<sup>*</sup>
                        </small>
                    </p>
                </h2>

                <li className='check'>
                    <Check />
                </li>
                <li className='check'>
                    <Check />
                </li>
                <li className='check'>
                    <Check />
                </li>
                <li className='check'>
                    <Check />
                </li>
                <li className='check'>
                    <Check />
                </li>
                <li className='check'>
                    <Check />
                </li>

                {times(5, (n) => (
                    <li key={n} />
                ))}

                <div className='cta'>
                    <a
                        onClick={() => {
                            ampli.clickSubscriptionPlanTable({
                                subscriptionPlanTableButton: 'Premium',
                                subscriptionPlanTableLocation: location,
                            });
                        }}
                        href={getGaiaPremiumURL}
                    >
                        Get Gaia GPS Premium
                    </a>
                </div>
            </ul>
            <ul className='features oplus'>
                <h2 className='col-heading'>
                    <img
                        width={152}
                        height={32}
                        className='logo'
                        src={gaiaPremium}
                        alt='Gaia logo'
                    />
                    <p
                        data-pw='oplus-price'
                        className={`price ${isOutsidePlusFullPrice ? '' : 'discounted'}`}
                    >
                        {formatPrice(outsidePlusMonthlyPrice)}
                        <small>
                            <b>/</b>mo<sup>**</sup>
                        </small>
                    </p>
                </h2>
                <li className='check'>
                    <Check />
                </li>
                <li className='check'>
                    <Check />
                </li>
                <li className='check'>
                    <Check />
                </li>
                <li className='check'>
                    <Check />
                </li>
                <li className='check'>
                    <Check />
                </li>
                <li className='check'>
                    <Check />
                </li>
                <li className='check oplus'>
                    <Check />
                </li>
                <li className='check oplus'>
                    <Check />
                </li>
                <li className='check oplus'>
                    <Check />
                </li>
                <li className='check oplus'>
                    <Check />
                </li>
                <li className='check oplus'>
                    <Check />
                </li>

                <div className='cta'>
                    <a
                        onClick={() => {
                            ampli.clickSubscriptionPlanTable({
                                subscriptionPlanTableButton: 'Outside+',
                                subscriptionPlanTableLocation: location,
                            });
                        }}
                        href={getOutsidePlusURL}
                    >
                        Get Outside+
                    </a>
                </div>
            </ul>
            <aside className='highlight'>
                <p>
                    <img src={outsidePlusBlackText} alt='Outside Plus icon' />
                </p>
            </aside>
            <small className='legal'>
                <>
                    *Billed annually; {formatPrice(gaiaPremiumAnnualPrice)}{' '}
                    {isGaiaPremiumFullPrice ? 'per year' : 'for the first year'}.{' **'}
                </>
                Billed annually; {formatPrice(outsidePlusAnnualPrice)} for the first year.
            </small>
        </section>
    );
}

function Check() {
    return (
        <svg width='24' height='19' viewBox='0 0 24 19'>
            <path
                d='M20.8261 0.833008L24 4.00697L9.15311 18.8539L0 9.70094L3.17386 6.52698L9.15311 12.506L20.8261 0.833008Z'
                fill='currentColor'
            />
        </svg>
    );
}
